import {memo} from "react";
import {Badge, Box, Flex, HStack, Heading, Icon} from "@chakra-ui/react";
import {ArrowDownTrayIcon, ChevronDownIcon, LockClosedIcon, LockOpenIcon} from "@heroicons/react/16/solid";
import type {ElcHeaderProps, ProcessStatus, ReportOption, Selection} from "../types";
import {STATUS_LABELS, getAvailableReports} from "../types";
import {ENTITY_LEVEL_CONTROLS, FINANCIAL_STATEMENT_AREAS, PROCESSES} from "../mockData";
import {Button} from "../../../../../components/ui/button";
import {MenuContent, MenuItem, MenuRoot, MenuTrigger} from "../../../../../components/ui/menu";
import {Tooltip} from "../../../../../components/ui/tooltip";
import {ReviewsWidget, CommentsWidget} from "./CollaborationWidget";

function resolveEntityName(type: "elc" | "process" | "fsa", id: string): string {
  if (type === "elc") return ENTITY_LEVEL_CONTROLS.find(e => e.id === id)?.name ?? id;
  if (type === "fsa") return FINANCIAL_STATEMENT_AREAS.find(f => f.id === id)?.name ?? id;
  return PROCESSES.find(p => p.id === id)?.name ?? id;
}

const STATUS_OPTIONS: ProcessStatus[] = ["not-ready", "in-progress", "in-review", "complete"];

export const StatusButton = memo(
  ({status, onStatusChange}: {status: ProcessStatus; onStatusChange: (s: ProcessStatus) => void}) => (
    <MenuRoot>
      <MenuTrigger asChild>
        <Button size="xs" variant="outline" colorPalette="gray" bg="white">
          {STATUS_LABELS[status]}
          <Icon asChild boxSize="3.5">
            <ChevronDownIcon />
          </Icon>
        </Button>
      </MenuTrigger>
      <MenuContent>
        {STATUS_OPTIONS.map(s => (
          <MenuItem key={s} value={s} onClick={() => onStatusChange(s)}>
            {STATUS_LABELS[s]}
          </MenuItem>
        ))}
      </MenuContent>
    </MenuRoot>
  ),
);

export const LockToggle = memo(
  ({
    locked,
    lockedBy,
    lockedAt,
    onToggle,
  }: {
    locked: boolean;
    lockedBy?: string;
    lockedAt?: Date;
    onToggle: (locked: boolean) => void;
  }) => {
    const tooltipContent =
      locked && lockedBy ? `Locked by ${lockedBy}${lockedAt ? ` on ${lockedAt.toLocaleDateString()}` : ""}` : undefined;

    const button = locked ? (
      <Button
        size="xs"
        variant="solid"
        bg="yellow.500"
        color="white"
        _hover={{bg: "yellow.600"}}
        onClick={() => onToggle(false)}
      >
        <Icon asChild boxSize="3.5">
          <LockClosedIcon />
        </Icon>
        Locked
      </Button>
    ) : (
      <Button size="xs" variant="outline" colorPalette="gray" bg="white" onClick={() => onToggle(true)}>
        <Icon asChild boxSize="3.5">
          <LockOpenIcon />
        </Icon>
        Lock
      </Button>
    );

    if (tooltipContent) {
      return (
        <Tooltip content={tooltipContent} openDelay={200}>
          {button}
        </Tooltip>
      );
    }
    return button;
  },
);

export const ExportButton = memo(
  ({selection, onOpenReport}: {selection: Selection; onOpenReport: (report: ReportOption) => void}) => {
    const reports = getAvailableReports(selection, resolveEntityName);
    return (
      <MenuRoot>
        <MenuTrigger asChild>
          <Button size="xs" variant="outline" colorPalette="gray" bg="white">
            <Icon asChild boxSize="3.5">
              <ArrowDownTrayIcon />
            </Icon>
            Export
            <Icon asChild boxSize="3.5">
              <ChevronDownIcon />
            </Icon>
          </Button>
        </MenuTrigger>
        <MenuContent maxW="xs">
          {reports.map(report => (
            <MenuItem
              key={report.key}
              value={report.key}
              disabled={!report.enabled}
              onClick={() => report.enabled && onOpenReport(report)}
            >
              <HStack justify="space-between" w="full">
                {report.label}
                {!report.enabled && (
                  <Badge variant="subtle" size="xs">
                    Coming soon
                  </Badge>
                )}
              </HStack>
            </MenuItem>
          ))}
        </MenuContent>
      </MenuRoot>
    );
  },
);

export const ElcHeader = memo(({elc, ...collaborationProps}: ElcHeaderProps) => {
  const {
    status,
    onStatusChange,
    locked,
    lockedBy,
    lockedAt,
    onLockChange,
    signOffs,
    messages,
    unreadCount,
    onOpenCollaboration,
    selection,
    onOpenReport,
  } = collaborationProps;
  return (
    <Box px={6} py={4} borderBottom="1px solid {colors.gray.200}" bg="white">
      <Flex align="center" gap={2}>
        <Heading size="sm" color="gray.800" flex={1}>
          {elc.name}
        </Heading>
        <ReviewsWidget signOffs={signOffs} onOpen={onOpenCollaboration} />
        <CommentsWidget messages={messages} unreadCount={unreadCount} onOpen={onOpenCollaboration} />
        <StatusButton status={status} onStatusChange={onStatusChange} />
        <LockToggle locked={locked} lockedBy={lockedBy} lockedAt={lockedAt} onToggle={onLockChange} />
        <ExportButton selection={selection} onOpenReport={onOpenReport} />
      </Flex>
    </Box>
  );
});
